/**
 * core/glambox/GlamboxLoginHandler
 * ----------------------------------------------------------------------
 * Classe utilizada para realizar autenticação de forma desacoplada,
 * 
 * @author      Fabio Y. Goto <fabio.goto@b4agroup.com>
 * @since       0.0.1
 */
export class GlamboxLoginHandler {
  // PROPRIEDADES ESTÁTICAS
  // --------------------------------------------------------------------

  /**
   * Armazena uma instância, permitindo que esta classe seja um singleton.
   * 
   * @type {GlamboxLoginHandler}
   */
  static INSTANCE = null;

  /**
   * Usado para validar `baseUrl`.
   * 
   * @type {RegExp}
   */
  static URL_REGEX = /^(?:(?:(?:https?|ftp):)?\/\/)(?:\S+(?::\S*)?@)?(?:(?!(?:10|127)(?:\.\d{1,3}){3})(?!(?:169\.254|192\.168)(?:\.\d{1,3}){2})(?!172\.(?:1[6-9]|2\d|3[0-1])(?:\.\d{1,3}){2})(?:[1-9]\d?|1\d\d|2[01]\d|22[0-3])(?:\.(?:1?\d{1,2}|2[0-4]\d|25[0-5])){2}(?:\.(?:[1-9]\d?|1\d\d|2[0-4]\d|25[0-4]))|(?:(?:[a-z0-9\u00a1-\uffff][a-z0-9\u00a1-\uffff_-]{0,62})?[a-z0-9\u00a1-\uffff]\.)+(?:[a-z\u00a1-\uffff]{2,}\.?))(?::\d{2,5})?(?:[/?#]\S*)?$/i;

  /**
   * Usado para validar `username`.
   * 
   * @type {RegExp}
   */
  static MAIL_REGEX = /^\w+([^@]+)*@\w+([\.-]?\w+)*(\.\w{2,10})/i;

  // PROPRIEDADES PRIVADAS
  // --------------------------------------------------------------------

  /**
   * URL de base para request.
   * 
   * Precisa ser uma URL com protocolo.
   * 
   * @type {String}
   * @private
   */
  __root = null;

  /**
   * Caminho, dentro de `baseUrl` aonde se localiza o endpoint de login.
   * 
   * @type {String}
   * @private
   */
  __path = null;

  /**
   * Domínio ao qual os cookies de autenticação serão vinculados.
   * 
   * @type {String}
   * @private 
   */
  __domain = null;

  // LIFECYCLE
  // --------------------------------------------------------------------

  /**
   * Construtor.
   * 
   * @param {String} baseUrl 
   *     URL de base para executar requests 
   * @param {String} path 
   *     Caminho, dentro de `baseUrl` aonde o endpoint de login se encontra 
   * @param {String} domain 
   *     Domínio em que os cookies serão vinculados
   */
  constructor (baseUrl, path, domain = null) {
    if (GlamboxLoginHandler.INSTANCE !== null) {
      return GlamboxLoginHandler.INSTANCE;
    }

    try {
      if (!this._validateUrl(baseUrl.trim())) {
        throw new TypeError(
          `URL fornecida é inválida.`
        );
      }

      this.__root = baseUrl.trim().replace(/^\/|\/$/g, "");
      this.__path = (path !== "") 
        ? path.trim().replace(/^\/|\/$/g, "") 
        : path;
      this.__domain = domain;

      GlamboxLoginHandler.INSTANCE = this;
    } catch (e) {
      console.error(e);
    }
  }

  // MÉTODOS PÚBLICOS
  // --------------------------------------------------------------------
  
  /**
   * Executa o request de autenticação na API da Glambox utilizando `fetch`.
   * 
   * @param {String} username 
   *     Endereço de e-mail ou nome de usuário
   * @param {String} password 
   *     Duh!
   * @param {Function} callback 
   *     Opcional, callback que aceita apenas um parâmetro, executado após 
   *     o request ser finalizado. Caso seja fornecido, o método NÃO retorna 
   *     nada
   * @returns {Promise<T>|null|boolean}
   */
  async authenticate (username, password, callback = null) {
    username = username.trim();
    password = password.trim();

    if (!this._canIUseCookies()) return null;

    let requestData = {
          method: "POST",
          headers: {
            "Content-type": "application/json"
          },
          body: JSON.stringify({
            username,
            password
          })
        },
        responseData = {};

    let response = await fetch(
      this.getUrl(),
      requestData 
    );

    const {
      status,
      ok
    } = response;

    responseData.statusCode = status;

    if (status !== 200 && !ok) {
      responseData.result = new Error(
        "Não foi possível autenticar, por favor verifique o endereço de e-mail e senha digitados,"
      );
    } else {
      let body = await response.json();
  
      const {
        result
      } = await body;

      if (result) {
        const {
          Authorization,
          personId
        } = result;
  
        this.setCookie("gbAuthorization", Authorization);
        this.setCookie("gbPersonId", personId);

        if (
          !this.isCookieSet("gbAuthorization") 
            || !this.isCookieSet("gbPersonId")
        ) {
          responseData.result = new Error(
            "Não foi possível registrar os cookies, por favor verifique se o navegador possui cookies habilitados."
          );
        }

        responseData.result = result;
      }
    }

    if (callback && typeof callback === "function") {
      callback(responseData);
      return;
    }

    return responseData;
  }

  /**
   * Retorna a URL + caminho concatenados.
   * 
   * @returns {String}
   */
  getUrl () {
    return `${this.__root}/${this.__path}`;
  }

  /**
   * Verifica se um cookie já foi declarado.
   * 
   * @param {String} name 
   *     Nome do cookie para verificar
   */
  isCookieSet (name) {
    return (document.cookie.indexOf(name) !== -1);
  }

  /**
   * Define um cookie.
   * 
   * @param {String} name 
   *     Nome da chave do cookie
   * @param {String} value 
   *     Valor para definição
   */
  setCookie (name, value) {
    let date = new Date(Date.now() + (60 * 60 * 3 * 1000)),
        cookieObject = {
          [name]: value, 
          "path": "/",
          "domain": this.__domain,
          "expires": date.toUTCString()
        },
        cookie = [];
    
    for (let key of Object.keys(cookieObject)) {
      if (cookieObject[key]) cookie.push(`${key}=${cookieObject[key]}`);
    }

    document.cookie = cookie.join(";");
  }

  /**
   * Elimina todos os cookies de autenticação.
   */
  clearCookie () {
    this.expireCookie("gbAuthorization");
    this.expireCookie("gbPersonId");
  }

  /**
   * Expira/remove um cookie.
   * 
   * @param {String} name 
   *     Nome do cookie a ser expirado
   */
  expireCookie (name) {
    let date = new Date(1970, 0, 1, 0, 0, 0),
        cookieObject = {
          [name]: "", 
          "path": "/",
          "domain": this.__domain,
          "expires": date.toUTCString()
        },
        cookie = [];
    
    for (let key of Object.keys(cookieObject)) {
      if (cookieObject[key] !== null) cookie.push(`${key}=${cookieObject[key]}`);
    }

    document.cookie = cookie.join(";");
  }

  // MÉTODOS PRIVADOS
  // --------------------------------------------------------------------

  /**
   * Verifica se os cookies podem ser utilizados e estão ativos.
   * 
   * @returns {Boolean}
   */
  _canIUseCookies () {
    if (window && document && navigator) {
      let cookieEnabled = navigator.cookieEnabled,
          testCookie = "d" + Date.now();

      if (!cookieEnabled) {
        document.cookie = testCookie;
        cookieEnabled = document.cookie.indexOf(testCookie) !== -1;
      }

      return cookieEnabled;
    }
  }

  /**
   * Valida uma string de URL.
   * 
   * @param {String} url 
   *     URL para verificação
   * @returns {boolean}
   */
  _validateUrl (url) {
    if (/^https?:\/\/localhost:?([0-9]+)/.test(url)) {
      return true;
    }

    return GlamboxLoginHandler.URL_REGEX.test(url);
  }

  /**
   * Valida uma string de Email.
   * 
   * @param {String} email
   *     Email para verificação
   * @returns {boolean}
   */
  _validateEmail (email) {
    return GlamboxLoginHandler.MAIL_REGEX.test(email);
  }
}
